import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box, Stack, Paper, Typography, Chip, Button, TextField, Divider, InputAdornment, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import Iconify from "../../../components/color-utils/Iconify";
import {
  getLocationList, createLocation, updateLocation, deleteLocation,
} from "../../../redux/features/careerSlice";

// The places jobs are posted in.
//
// THE LIST LOOKS AFTER ITSELF. Posting a job with a place that is not on it yet
// adds it, so this screen is not data entry. It is here for the two things that
// cannot happen on their own: fixing a spelling, and retiring a place nobody
// uses any more.
//
// RENAMING IS THE POINT. Each job keeps a copy of the name so the screens can
// print it without a join, and a rename here rewrites that copy on every job
// pointing at this place. Correct it once, and every job is correct.
const CareerLocations = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { locationList, locationLoading } = useSelector((s) => s.career);

  // add-a-place box
  const [newName, setNewName] = useState("");
  // inline row edit
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => dispatch(getLocationList());
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (l) => {
    setEditingId(l.career_location_id);
    setEditName(l.location_name);
  };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  const add = () => {
    if (!newName.trim()) return enqueueSnackbar("Location name required", { variant: "warning" });
    setBusy(true);
    dispatch(createLocation({ location_name: newName.trim() }))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar("Location added", { variant: "success" });
          setNewName("");
          load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const saveEdit = () => {
    if (!editName.trim()) return enqueueSnackbar("Location name required", { variant: "warning" });
    setBusy(true);
    dispatch(updateLocation({ id: editingId, location_name: editName.trim() }))
      .then((res) => {
        if (res.payload?.status === "success") {
          // say how far the rename reached, so nobody has to go and check
          const fixed = res.payload?.data?.jobs_updated || 0;
          enqueueSnackbar(
            fixed
              ? `Renamed. ${fixed} ${fixed === 1 ? "job" : "jobs"} updated.`
              : "Renamed",
            { variant: "success" },
          );
          cancelEdit();
          load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const remove = (l) => {
    setBusy(true);
    dispatch(deleteLocation(l.career_location_id))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar("Location removed", { variant: "success" });
          if (l.career_location_id === editingId) cancelEdit();
          load();
        } else {
          // the server refuses to remove a place that is still in use, and says
          // how many jobs are on it. That message is more useful than "Failed".
          enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
        }
      })
      .finally(() => setBusy(false));
  };

  // client-side search by place name
  const q = search.trim().toLowerCase();
  const rows = (locationList || []).filter(
    (l) => !q || String(l.location_name || "").toLowerCase().includes(q),
  );

  return (
    <GlobalWrapper title={"Locations"}>
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The places jobs are posted in. This list fills itself: posting a job with a new place adds it here. Rename one to correct its spelling everywhere it is used. A place still used by a job cannot be removed.
        </Typography>

        {/* search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search locations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-outline" />
              </InputAdornment>
            ),
          }}
        />

        {/* table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ "& .MuiTableCell-root": { px: { xs: 0.75, sm: 2 } } }}>
            <TableHead>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell sx={{ width: { xs: 84, sm: 130 } }}>Jobs</TableCell>
                <TableCell align="right" sx={{ width: { xs: 90, sm: 190 } }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {locationLoading
                      ? "Loading…"
                      : q
                        ? "No locations match your search."
                        : "No locations yet. Post a job with a location and it appears here."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((l) => {
                const isEditing = l.career_location_id === editingId;
                const used = Number(l.job_count || 0);
                return (
                  <TableRow key={l.career_location_id} hover selected={isEditing}>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editName}
                          inputProps={{ maxLength: 120 }}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>{l.location_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={used ? "primary" : "default"}
                        label={used}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        sx={{ flexWrap: "wrap", gap: 1 }}
                      >
                        {isEditing ? (
                          <>
                            <Button size="small" variant="contained" disabled={busy} onClick={saveEdit}>
                              {busy ? "Saving…" : "Save"}
                            </Button>
                            <Button size="small" variant="outlined" disabled={busy} onClick={cancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <>
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => startEdit(l)}>Edit</Button>
                            <Button
                              size="small"
                              color="error"
                              // a place a job sits in is not removable, and saying so
                              // on hover beats letting somebody click and be refused
                              disabled={busy || used > 0}
                              title={used > 0 ? `Used by ${used} ${used === 1 ? "job" : "jobs"}` : ""}
                              onClick={() => remove(l)}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {editingId !== null && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Renaming a location also rewrites it on every job that uses it.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Add a location</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. Nilambur"
            value={newName}
            inputProps={{ maxLength: 120 }}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <Button
            variant="contained"
            disabled={busy}
            onClick={add}
            sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
          >
            {busy ? "Saving…" : "Add"}
          </Button>
        </Stack>
      </Box>
    </GlobalWrapper>
  );
};

export default CareerLocations;
